import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, getToken } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Outages() {
  const [status, setStatus] = useState(null);
  const [ops, setOps] = useState(null);
  const [stateFilter, setStateFilter] = useState("active");
  const [services, setServices] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ title: "", service_keys: [], location: "" });
  const [entry, setEntry] = useState("");
  const [major, setMajor] = useState({ commander_email: "", communication_cadence: "",
                                        bridge: "", clinical_impact: false });
  const [resolution, setResolution] = useState("");
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    apiFetch("/service-status").then((r) => (r.ok ? r.json() : null)).then(setStatus);
    apiFetch(`/outages/operations${stateFilter ? `?state=${stateFilter}` : ""}`).then(async (r) => {
      const body = await r.json();
      if (!r.ok) { setError("Outage operations unavailable"); return; }
      setOps(body);
      if (selected) {
        const fresh = body.outages.find((o) => o.id === selected.id);
        if (fresh) setSelected(fresh);
      }
    });
  }, [selected, stateFilter]);

  useEffect(() => {
    if (!getToken()) { navigate("/login"); return; }
    apiFetch("/catalog").then((r) => (r.ok ? r.json() : { services: [] }))
      .then((d) => setServices(d.services || []));
  }, [navigate]);

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ },
            [stateFilter]);

  const post = (path, body, label, then) =>
    apiFetch(path, { method: "POST", body: JSON.stringify(body) }).then(async (r) => {
      const out = await r.json();
      if (!r.ok) {
        setMessage(`${label} failed — ${typeof out.detail === "string" ? out.detail
                                                                       : JSON.stringify(out.detail)}`);
        return;
      }
      setMessage(`${label} done`);
      if (then) then(out);
      load();
    });

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" data-testid="outages-title">Service Status</h1>
          <p className="text-sm text-muted-foreground">
            What is down, and the desk that is working on it.
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm" data-testid="outages-calendar-link">
            <Link to="/calendar">Calendar</Link>
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-6 space-y-8">
        {error && <p className="text-sm text-destructive" data-testid="outages-error">{error}</p>}
        {message && <p className="text-sm text-amber-800" data-testid="outages-message">{message}</p>}

        {status && (
          <section className="space-y-3" data-testid="status-board">
            <h2 className="text-base md:text-lg font-medium">Right now</h2>
            <p className="text-sm text-muted-foreground" data-testid="status-note">
              {status.note}
            </p>
            {status.all_clear ? (
              <p className="text-sm text-emerald-800 border rounded-md px-3 py-2"
                 data-testid="status-all-clear">
                All services are running normally.
              </p>
            ) : (
              <ul className="space-y-2">
                {status.outages.map((o) => (
                  <li key={o.number} className="border rounded-md p-3 space-y-1"
                      data-testid={`status-row-${o.number}`}>
                    <p className="text-sm font-medium">
                      {o.title}
                      {o.major && (
                        <span className="ml-2 text-xs text-red-800"
                              data-testid={`status-major-${o.number}`}>major incident</span>
                      )}
                      {o.clinical_impact && (
                        <span className="ml-2 text-xs text-red-800">care affected</span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      <span className="font-mono">{o.number}</span> ·{" "}
                      {o.enterprise_wide ? "All sites" : o.location_name || o.location_key} ·{" "}
                      {o.services.join(", ") || "service not recorded"}
                    </p>
                    {o.last_update && (
                      <p className="text-sm" data-testid={`status-update-${o.number}`}>
                        Latest: {o.last_update}
                        <span className="text-xs text-muted-foreground">
                          {" "}({new Date(o.last_update_at).toLocaleString()})
                        </span>
                      </p>
                    )}
                    {o.workaround && (
                      <p className="text-sm" data-testid={`status-workaround-${o.number}`}>
                        What to do instead: {o.workaround}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {ops?.can_declare && (
          <section className="space-y-3 border-t pt-6" data-testid="outage-declare">
            <h2 className="text-base md:text-lg font-medium">Declare an outage</h2>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <label htmlFor="outage-title" className="text-sm font-medium">
                  What is happening?
                </label>
                <Input id="outage-title" data-testid="outage-title" value={form.title}
                       placeholder="Printing unavailable — North Clinic"
                       onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div className="space-y-1">
                <label htmlFor="outage-site" className="text-sm font-medium">Site affected</label>
                <select id="outage-site" data-testid="outage-site" value={form.location}
                        className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                        onChange={(e) => setForm({ ...form, location: e.target.value })}>
                  <option value="">All sites</option>
                  {(ops.sites || []).map((s) => (
                    <option key={s.key} value={s.key}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1 md:col-span-2">
                <label htmlFor="outage-services" className="text-sm font-medium">
                  Services affected
                </label>
                <select id="outage-services" data-testid="outage-services" multiple
                        className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                        value={form.service_keys}
                        onChange={(e) => setForm({ ...form,
                                                   service_keys: Array.from(e.target.selectedOptions)
                                                     .map((o) => o.value) })}>
                  {services.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
                </select>
              </div>
            </div>
            <Button size="sm" data-testid="outage-declare-submit"
                    onClick={() => post("/outages",
                                         { title: form.title, service_keys: form.service_keys,
                                           location: form.location || null },
                                         "Outage declared",
                                         () => setForm({ title: "", service_keys: [],
                                                         location: "" }))}>
              Declare outage
            </Button>
          </section>
        )}

        {ops && (
          <section className="space-y-2 border-t pt-6" data-testid="outage-operations">
            <h2 className="text-base md:text-lg font-medium">
              Operations view ({ops.totals.active} active)
            </h2>
            <div className="space-y-1 max-w-xs">
              <label htmlFor="outage-state-filter" className="text-sm font-medium">Showing</label>
              <select id="outage-state-filter" data-testid="outage-state-filter"
                      className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                      value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
                <option value="active">Active only</option>
                <option value="resolved">Resolved</option>
                <option value="">Everything</option>
              </select>
            </div>
            <ul className="space-y-1">
              {ops.outages.map((o) => (
                <li key={o.id}>
                  <button className="w-full text-left text-sm border rounded px-3 py-2 hover:border-foreground/40"
                          data-testid={`outage-row-${o.number}`} onClick={() => setSelected(o)}>
                    <span className="font-mono">{o.number}</span> · {o.title} ·{" "}
                    <span className="text-muted-foreground">{o.state}</span>
                    {o.major && <span className="text-red-800"> · major</span>}
                  </button>
                </li>
              ))}
              {ops.outages.length === 0 && (
                <li className="text-sm text-muted-foreground" data-testid="outage-operations-empty">
                  No outages recorded.
                </li>
              )}
            </ul>
          </section>
        )}

        {selected && (
          <section className="space-y-4 border-t pt-6" data-testid="outage-detail">
            <div className="flex items-center justify-between">
              <h2 className="text-base md:text-lg font-medium" data-testid="outage-detail-title">
                {selected.number} — {selected.title}
              </h2>
              <Button size="sm" variant="ghost" data-testid="outage-detail-close"
                      onClick={() => setSelected(null)}>Close</Button>
            </div>
            <p className="text-sm" data-testid="outage-detail-meta">
              {selected.state}
              {selected.major && " · major incident"}
              {selected.clinical_impact && " · care affected"}
              {selected.commander && ` · commander ${selected.commander.name}`}
              {selected.communication_cadence && ` · updates ${selected.communication_cadence}`}
              {selected.problem_number && ` · problem ${selected.problem_number}`}
            </p>

            <div>
              <h3 className="text-sm font-semibold">Timeline</h3>
              <ol className="text-sm space-y-1" data-testid="outage-timeline">
                {(selected.timeline || []).map((t, i) => (
                  <li key={i}>
                    <span className="text-muted-foreground">
                      {new Date(t.at).toLocaleString()}:
                    </span>{" "}
                    {t.entry}
                  </li>
                ))}
              </ol>
            </div>

            {ops?.can_declare && selected.state === "active" && (
              <div className="grid gap-4 md:grid-cols-2">
                <div className="border rounded-md p-4 space-y-2">
                  <h3 className="text-sm font-semibold">Post an update</h3>
                  <Input placeholder="What clinics need to know now" value={entry}
                         data-testid="outage-update-input"
                         onChange={(e) => setEntry(e.target.value)} />
                  <Button size="sm" variant="outline" data-testid="outage-update-post"
                          onClick={() => post(`/outages/${selected.id}/timeline`,
                                               { entry }, "Update posted",
                                               () => setEntry(""))}>
                    Post update
                  </Button>

                  <h3 className="text-sm font-semibold pt-2">Resolve</h3>
                  <Input placeholder="What fixed it" value={resolution}
                         data-testid="outage-resolution-input"
                         onChange={(e) => setResolution(e.target.value)} />
                  <Button size="sm" variant="outline" data-testid="outage-resolve"
                          onClick={() => post(`/outages/${selected.id}/resolve`,
                                               { resolution }, "Outage resolved",
                                               () => setResolution(""))}>
                    Mark resolved
                  </Button>
                </div>

                {!selected.major && (
                  <div className="border rounded-md p-4 space-y-2">
                    <h3 className="text-sm font-semibold">Declare a major incident</h3>
                    <Input placeholder="Commander email" value={major.commander_email}
                           data-testid="outage-commander-input"
                           onChange={(e) => setMajor({ ...major,
                                                       commander_email: e.target.value })} />
                    <Input placeholder="Update cadence, e.g. every 30 minutes"
                           value={major.communication_cadence} data-testid="outage-cadence-input"
                           onChange={(e) => setMajor({ ...major,
                                                       communication_cadence: e.target.value })} />
                    <Input placeholder="Bridge details" value={major.bridge}
                           data-testid="outage-bridge-input"
                           onChange={(e) => setMajor({ ...major, bridge: e.target.value })} />
                    <div className="flex items-center gap-2">
                      <input id="outage-clinical" type="checkbox" checked={major.clinical_impact}
                             data-testid="outage-clinical-input"
                             onChange={(e) => setMajor({ ...major,
                                                         clinical_impact: e.target.checked })} />
                      <label htmlFor="outage-clinical" className="text-sm">
                        Patient care is affected
                      </label>
                    </div>
                    <Button size="sm" variant="outline" data-testid="outage-declare-major"
                            onClick={() => post(`/outages/${selected.id}/declare-major`, major,
                                                 "Major incident declared")}>
                      Declare major
                    </Button>
                  </div>
                )}
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
